import { toRFC3339 } from "../utils/time";
import { fetchJson } from "./http";
import type { LogRecord, ServiceSlug } from "./types";

export interface GetLogsForTraceParams {
  serviceSlug: ServiceSlug;
  traceId: string;
  startTime?: Date;
  endTime?: Date;
  severityRanges?: string[];
  limit?: number;
  signal?: AbortSignal;
}

export async function getLogsForTrace({
  serviceSlug,
  traceId,
  startTime,
  endTime,
  severityRanges,
  limit,
  signal,
}: GetLogsForTraceParams): Promise<LogRecord[]> {
  const result = await fetchJson<LogRecord[] | null>(
    `/traces/${encodeURIComponent(traceId)}/logs`,
    {
      serviceSlug,
      signal,
      searchParams: {
        start_time: startTime ? toRFC3339(startTime) : undefined,
        end_time: endTime ? toRFC3339(endTime) : undefined,
        severity_ranges:
          severityRanges && severityRanges.length > 0 ? severityRanges.join(",") : undefined,
        limit,
      },
    },
  );
  return Array.isArray(result) ? result : [];
}

export type BodyFilterMode = "contains" | "prefix" | "suffix" | "equals";

export interface BodyFilter {
  text: string;
  mode: BodyFilterMode;
}

export interface GetLogsParams {
  serviceSlug: ServiceSlug;
  startTime?: Date;
  endTime?: Date;
  serviceNames?: string[];
  serviceInstances?: string[];
  traceIds?: string[];
  spanIds?: string[];
  logIds?: string[];
  severityRanges?: string[];
  bodyFilters?: BodyFilter[];
  sort?: string;
  order?: string;
  limit?: number;
  signal?: AbortSignal;
}

export async function getLogs({
  serviceSlug,
  startTime,
  endTime,
  serviceNames = [],
  serviceInstances = [],
  traceIds = [],
  spanIds = [],
  logIds = [],
  severityRanges = [],
  bodyFilters = [],
  sort,
  order,
  limit,
  signal,
}: GetLogsParams): Promise<LogRecord[]> {
  const searchParams: Record<string, string | undefined> = {
    start_time: startTime ? toRFC3339(startTime) : undefined,
    end_time: endTime ? toRFC3339(endTime) : undefined,
    service_names: serviceNames.length > 0 ? serviceNames.join(",") : undefined,
    service_instances: serviceInstances.length > 0 ? serviceInstances.join(",") : undefined,
    traceIds: traceIds.length > 0 ? traceIds.join(",") : undefined,
    spanIds: spanIds.length > 0 ? spanIds.join(",") : undefined,
    logIds: logIds.length > 0 ? logIds.join(",") : undefined,
    severity_ranges: severityRanges.length > 0 ? severityRanges.join(",") : undefined,
    sort,
    order,
    limit,
  };

  const finalParams: Record<string, string | string[] | undefined> = { ...searchParams };
  if (bodyFilters.length > 0) {
    finalParams.body_filter = bodyFilters.map((f) => `${f.mode}:${f.text}`);
  }

  const result = await fetchJson<LogRecord[] | null>("/logs", {
    serviceSlug,
    signal,
    searchParams: finalParams,
  });
  return Array.isArray(result) ? result : [];
}

export interface GetLogParams {
  serviceSlug: ServiceSlug;
  logId: string;
  signal?: AbortSignal;
}

export async function getLog({
  serviceSlug,
  logId,
  signal,
}: GetLogParams): Promise<LogRecord | null> {
  return await fetchJson<LogRecord | null>(`/logs/${encodeURIComponent(logId)}`, {
    serviceSlug,
    signal,
  });
}

export interface GetLogsForSpanParams {
  serviceSlug: ServiceSlug;
  traceId: string;
  spanId: string;
  startTime?: Date;
  endTime?: Date;
  severityRanges?: string[];
  limit?: number;
  signal?: AbortSignal;
}

export async function getLogsForSpan({
  serviceSlug,
  traceId,
  spanId,
  startTime,
  endTime,
  severityRanges,
  limit,
  signal,
}: GetLogsForSpanParams): Promise<LogRecord[]> {
  const result = await fetchJson<LogRecord[] | null>(
    `/traces/${encodeURIComponent(traceId)}/spans/${encodeURIComponent(spanId)}/logs`,
    {
      serviceSlug,
      signal,
      searchParams: {
        start_time: startTime ? toRFC3339(startTime) : undefined,
        end_time: endTime ? toRFC3339(endTime) : undefined,
        severity_ranges:
          severityRanges && severityRanges.length > 0 ? severityRanges.join(",") : undefined,
        limit,
      },
    },
  );
  return Array.isArray(result) ? result : [];
}
